import React, { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import axios from "axios";
import {
    BuildingOffice2Icon,
    EnvelopeIcon,
    PhoneIcon,
} from "@heroicons/react/24/outline";
import Turnstile from "../turnstile/Turnstile";
import { getLocation } from "../../config/location";

// Validation schema with Yup
const validationSchema = Yup.object().shape({
    firstName: Yup.string()
        .required("Vorname ist erforderlich")
        .min(2, "Vorname muss mindestens 2 Zeichen lang sein"),
    lastName: Yup.string()
        .required("Nachname ist erforderlich")
        .min(2, "Nachname muss mindestens 2 Zeichen lang sein"),
    email: Yup.string()
        .required("Email ist erforderlich")
        .email("Ungültige Email-Adresse"),
    phoneNumber: Yup.string()
        .required("Telefonnummer ist erforderlich")
        .matches(/^[0-9\s-]+$/, "Ungültige Telefonnummer"),
    message: Yup.string()
        .required("Nachricht ist erforderlich")
        .min(10, "Nachricht muss mindestens 10 Zeichen lang sein"),
    website: Yup.string().max(0, "Ungültige Anfrage"),
    formStartedAt: Yup.number().required(),
    turnstileToken: Yup.string().test(
        "turnstile-required",
        "Bitte bestätigen Sie, dass Sie kein Roboter sind.",
        (value) => !process.env.REACT_APP_TURNSTILE_SITE_KEY || Boolean(value),
    ),
});

export default function Example() {
    const location = getLocation();
    const [successMessage, setSuccessMessage] = useState(null);
    const [errorMessage, setErrorMessage] = useState(null);
    const turnstileRef = useRef(null);

    const {
        register,
        handleSubmit,
        setValue,
        formState: { errors },
        reset,
    } = useForm({
        resolver: yupResolver(validationSchema),
    });

    useEffect(() => {
        setValue("formStartedAt", Date.now());
        setValue("website", "");
    }, [setValue]);

    const onSubmit = async (data) => {
        try {
            await axios.post("/api/public/contact", data);
            setSuccessMessage(
                "Ihre Anfrage wurde erfolgreich gesendet. Wir melden uns bei Ihnen.",
            );
            reset();
            setValue("formStartedAt", Date.now());
            turnstileRef.current?.reset();
        } catch (error) {
            setErrorMessage(
                "Fehler beim Senden der Anfrage. Bitte versuchen Sie es erneut." +
                    error.message,
            );
            turnstileRef.current?.reset();
        }
    };

    return (
        <div className="relative w-full isolate bg-white" id="kontaktanfrage">
            <div className="mx-auto grid max-w-7xl grid-cols-1 lg:grid-cols-2">
                <div className="relative px-6 pt-24 sm:pt-32 lg:static lg:px-8 lg:py-48">
                    <div className="mx-auto max-w-xl lg:mx-0 lg:max-w-lg">
                        <h2 className="text-pretty text-4xl font-semibold tracking-tight text-gray-900 sm:text-5xl">
                            Nehmen Sie Kontakt mit uns auf
                        </h2>
                        <p className="mt-6 text-lg/8 text-gray-600">
                            Wir freuen uns auf Ihre Anfrage. Füllen Sie einfach
                            das Formular aus und wir melden uns bei Ihnen.
                        </p>
                        <dl className="mt-10 space-y-4 text-base/7 text-gray-600">
                            <div className="flex gap-x-4">
                                <dt className="flex-none">
                                    <BuildingOffice2Icon
                                        aria-hidden="true"
                                        className="h-7 w-6 text-gray-400"
                                    />
                                </dt>
                                <dd>
                                    {location.venue && (
                                        <>
                                            {location.venue}
                                            <br />
                                        </>
                                    )}
                                    {location.street}
                                    <br />
                                    {location.city}
                                </dd>
                            </div>
                            <div className="flex gap-x-4">
                                <dt className="flex-none">
                                    <PhoneIcon
                                        aria-hidden="true"
                                        className="h-7 w-6 text-gray-400"
                                    />
                                </dt>
                                <dd>
                                    <a
                                        href="tel:+4972449469991"
                                        className="hover:text-gray-900"
                                    >
                                        07244 9469991
                                    </a>
                                </dd>
                            </div>
                            <div className="flex gap-x-4">
                                <dt className="flex-none">
                                    <EnvelopeIcon
                                        aria-hidden="true"
                                        className="h-7 w-6 text-gray-400"
                                    />
                                </dt>
                                <dd>
                                    <a
                                        href="mailto:info@en-edem.de"
                                        className="hover:text-gray-900"
                                    >
                                        info@en-edem.de
                                    </a>
                                </dd>
                            </div>
                        </dl>
                    </div>
                </div>
                <form
                    onSubmit={handleSubmit(onSubmit)}
                    className="px-6 pb-24 pt-20 sm:pb-32 lg:px-8 lg:py-48"
                >
                    <div className="mx-auto max-w-xl lg:mr-0 lg:max-w-lg">
                        {successMessage && (
                            <div className="mb-6 rounded-md bg-green-100 p-4 text-green-700">
                                {successMessage}
                            </div>
                        )}
                        <div className="grid grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-2">
                            <input
                                type="text"
                                tabIndex={-1}
                                autoComplete="off"
                                {...register("website")}
                                className="hidden"
                                aria-hidden="true"
                            />
                            <input
                                type="hidden"
                                {...register("formStartedAt", {
                                    valueAsNumber: true,
                                })}
                            />
                            <div>
                                <label
                                    htmlFor="firstName"
                                    className="block text-sm/6 font-semibold text-gray-900"
                                >
                                    Vorname
                                </label>
                                <input
                                    id="firstName"
                                    type="text"
                                    {...register("firstName")}
                                    className={`block w-full rounded-md bg-white px-3.5 py-2 text-base text-gray-900 outline outline-1 -outline-offset-1 ${
                                        errors.firstName
                                            ? "outline-red-500 focus:outline-red-500"
                                            : "outline-gray-300 focus:outline-indigo-600"
                                    }`}
                                />
                                {errors.firstName && (
                                    <p className="text-sm text-red-600">
                                        {errors.firstName.message}
                                    </p>
                                )}
                            </div>
                            <div>
                                <label
                                    htmlFor="lastName"
                                    className="block text-sm/6 font-semibold text-gray-900"
                                >
                                    Nachname
                                </label>
                                <input
                                    id="lastName"
                                    type="text"
                                    {...register("lastName")}
                                    className={`block w-full rounded-md bg-white px-3.5 py-2 text-base text-gray-900 outline outline-1 -outline-offset-1 ${
                                        errors.lastName
                                            ? "outline-red-500 focus:outline-red-500"
                                            : "outline-gray-300 focus:outline-indigo-600"
                                    }`}
                                />
                                {errors.lastName && (
                                    <p className="text-sm text-red-600">
                                        {errors.lastName.message}
                                    </p>
                                )}
                            </div>
                            <div className="sm:col-span-2">
                                <label
                                    htmlFor="email"
                                    className="block text-sm/6 font-semibold text-gray-900"
                                >
                                    Email
                                </label>
                                <input
                                    id="email"
                                    type="email"
                                    {...register("email")}
                                    className={`block w-full rounded-md bg-white px-3.5 py-2 text-base text-gray-900 outline outline-1 -outline-offset-1 ${
                                        errors.email
                                            ? "outline-red-500 focus:outline-red-500"
                                            : "outline-gray-300 focus:outline-indigo-600"
                                    }`}
                                />
                                {errors.email && (
                                    <p className="text-sm text-red-600">
                                        {errors.email.message}
                                    </p>
                                )}
                            </div>
                            <div className="sm:col-span-2">
                                <label
                                    htmlFor="phoneNumber"
                                    className="block text-sm/6 font-semibold text-gray-900"
                                >
                                    Telefonnummer
                                </label>
                                <input
                                    id="phoneNumber"
                                    type="tel"
                                    {...register("phoneNumber")}
                                    className={`block w-full rounded-md bg-white px-3.5 py-2 text-base text-gray-900 outline outline-1 -outline-offset-1 ${
                                        errors.phoneNumber
                                            ? "outline-red-500 focus:outline-red-500"
                                            : "outline-gray-300 focus:outline-indigo-600"
                                    }`}
                                />
                                {errors.phoneNumber && (
                                    <p className="text-sm text-red-600">
                                        {errors.phoneNumber.message}
                                    </p>
                                )}
                            </div>
                            <div className="sm:col-span-2">
                                <label
                                    htmlFor="message"
                                    className="block text-sm/6 font-semibold text-gray-900"
                                >
                                    Nachricht
                                </label>
                                <textarea
                                    id="message"
                                    rows={4}
                                    {...register("message")}
                                    className={`block w-full rounded-md bg-white px-3.5 py-2 text-base text-gray-900 outline outline-1 -outline-offset-1 ${
                                        errors.message
                                            ? "outline-red-500 focus:outline-red-500"
                                            : "outline-gray-300 focus:outline-indigo-600"
                                    }`}
                                />
                                {errors.message && (
                                    <p className="text-sm text-red-600">
                                        {errors.message.message}
                                    </p>
                                )}
                            </div>
                        </div>
                        <div className="mt-6">
                            <Turnstile
                                ref={turnstileRef}
                                onVerify={(token) =>
                                    setValue("turnstileToken", token, {
                                        shouldValidate: true,
                                    })
                                }
                                onExpire={() =>
                                    setValue("turnstileToken", "", {
                                        shouldValidate: false,
                                    })
                                }
                            />
                            {errors.turnstileToken && (
                                <p className="mt-2 text-sm text-red-600">
                                    {errors.turnstileToken.message}
                                </p>
                            )}
                        </div>
                        <div className="mt-8 flex justify-start md:justify-end">
                            <button
                                type="submit"
                                className="rounded-md bg-enedemgreen px-3.5 py-2.5 text-center text-sm font-semibold text-white shadow-sm hover:bg-green-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                            >
                                Anfrage absenden
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
}
